import { Image, InputGroup, Spinner } from "react-bootstrap";
import awayterImg from '../../utils/resources/awayter_connect_light_cropped.png';
import { ErrorMessage, Field, Form, Formik, useFormikContext } from "formik";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { hash } from "bcryptjs";
import { signupFinalUser } from "../../utils/services/user.service";
import * as Yup from 'yup';
import { passwordRegex, UserLanguages } from "../../utils/const";
import './Signup.scss'
import { NotificationToast } from "aw-notificationtoast";
import { AWSelect } from "aw-select";
import { useTheme } from '../../utils/contexts/ThemeContext';

// Esquema de validación con Yup
const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    surname: Yup.string().required('Surname is required'),
    companyStr: Yup.string().required('Company is required'),
    email: Yup.string()
        .email('Invalid email format')
        .required('Email is required'),
    phone: Yup.string()
        .required('Phone is required')
        .matches(/^(?:\+?\d{1,4} ?)?\d{9,}$/, 'Phone number must be at least 9 digits, numbers and letters'), //Pot tenir prefixos al principi, minim 9 numeros
    password: Yup.string()
        .matches(passwordRegex, 'Password must contain at least 8 characters')
        .required('Password is required'),
    policy: Yup.boolean()
        .oneOf([true], 'You must accept the policies')
        .required('You must accept the policies')
});

// Definición de la interfaz de los valores del formulario
interface FormValues {
    name: string;
    surname: string;
    companyStr: string;
    email: string;
    phone: string;
    password: string;
    language: number;
    policy: boolean;
}

// Interfaz para las props del componente InnerForm
interface InnerFormProps {
    disableLoginBtn: boolean;
    errorSignup: { error: boolean; msg: string };
    setErrorSignup: React.Dispatch<React.SetStateAction<{ error: boolean; msg: string }>>;
    navigate: (path: string) => void;
    UserLang: { value: number; label: string }[];
}

// Componente interno que consume el contexto de Formik
const InnerForm: React.FC<InnerFormProps> = ({ disableLoginBtn, navigate, UserLang }) => {
    const { errors, touched, setFieldValue } = useFormikContext<FormValues>();
    const theme = useTheme()
    const [passwordVisibility, setPasswordVisibility] = useState(false);
    return (
        <Form className='form mt-2' autoComplete="off">
            <div className="text-start mb-3">
                <Field
                    className={`form-control ${errors.name && touched.name ? 'is-invalid' : ''}`}
                    type="text"
                    placeholder='Name*'
                    name="name"
                />
            </div>
            <div className="text-start mb-3">
                <Field
                    className={`form-control ${errors.surname && touched.surname ? 'is-invalid' : ''}`}
                    type="text"
                    placeholder='Surname*'
                    name="surname"
                />
            </div>
            <div className="text-start mb-3">
                <Field
                    className={`form-control ${errors.companyStr && touched.companyStr ? 'is-invalid' : ''}`}
                    type="text"
                    placeholder='Company*'
                    name="companyStr"
                />
            </div>
            <div className="text-start mb-3">
                <Field
                    type="text"
                    placeholder='Phone number*'
                    name="phone"
                    className={`form-control ${errors.phone && touched.phone ? 'is-invalid' : ''}`}
                />
                <ErrorMessage name="phone" component="div" className="text-danger" />
            </div>
            <div className="text-start mb-3">
                <Field
                    className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                    type="email"
                    placeholder='Email*'
                    name="email"
                />
            </div>
            <div className='text-start mb-3'>
                <InputGroup className='password' >
                <Field type={passwordVisibility ? 'text' : 'password'} placeholder='Password*' name='password' className={`form-control ${errors.password && touched.password ? "is-invalid" : ""}`} />
                <InputGroup.Text onClick={() => setPasswordVisibility(!passwordVisibility)} >
                        <i className={passwordVisibility ? "bi bi-eye-slash" : "bi bi-eye"} >
                        </i>
                    </InputGroup.Text>
                </InputGroup>
                <ErrorMessage name='password' component='div' className='text-danger' />
            </div>
            <div className="mb-3 text-start">
                <label>
                    <Field className='me-2' type="checkbox" name="policy" />
                    I have read and agree to <a target='_blank' rel='noreferrer' href='https://privacy.awayter.com/'>privacy policy</a>*
                </label>
                <ErrorMessage name="policy" component="div" className="text-danger" />
            </div>
            <hr />
            <div className="mb-3 text-start">
                <label className='me-2' htmlFor="language">Default language: </label>
                <AWSelect
                    className="selectLanguage"
                    name='language'
                    closeMenuOnSelect={true}
                    defaultValue={UserLang[2]}
                    options={UserLang}
                    dark = {theme.theme === "dark"}
                    onChange={(selectedOption) => setFieldValue('language', selectedOption?.value)}
                />
            </div>
            <div className="text-center">
                <button disabled={disableLoginBtn} className='mb-2 btn-IDGreen btn-Custom' id='signupButton' type="submit">Create account</button>
                <hr />
                <button className='mb-2 btn-Custom text-black' style={{backgroundColor:"var(--IDGrey)"}} id='cancelButton' onClick={() => navigate('/login')} type="button">
                    Cancel
                </button>
            </div>
        </Form>
    );
};

// Componente principal de registro
const SignUpForm: React.FC = () => {
    const [errorSignup, setErrorSignup] = useState({ error: false, msg: '' });
    const navigate = useNavigate();
    const [disableLoginBtn, setDisableLoginBtn] = useState(false);
    const UserLang = UserLanguages();
    const [sendButtonCick, setSendButtonClick] = useState(false)
    const [showSpinner, setShowSpinner] = useState(true);
    const [showToast, setShowToast] = useState(false);

    useEffect(() => {
        if (sendButtonCick) {
            const timer = setTimeout(() => {
                setShowSpinner(false)
                navigate('/login')
            }, 20000)
            return () => clearTimeout(timer);
        }
    }, [sendButtonCick])

    useEffect(() => {
        if (errorSignup.error) {
            setShowToast(true);
            const timer = setTimeout(() => setShowToast(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [errorSignup.error]);

    // Use effect separats per 
    useEffect(() => {
        if (!showToast && errorSignup.error) {
            const timer = setTimeout(() =>setErrorSignup({ error: false, msg: "" }),200);
            return () => clearTimeout(timer);
        }
    }, [showToast]);


    const initialValues: FormValues = {
        name: '',
        surname: '',
        email: '',
        phone: '',
        companyStr: '',
        password: '',
        language: 0,
        policy: false
    };

    const handleSubmit = async (values: FormValues) => {
        setDisableLoginBtn(true);
        const {policy, ...newFormValues } = values;
        newFormValues.password = await hash(newFormValues.password, 10);
        signupFinalUser(newFormValues)
            .then((json: any) => {
                if (json.code !== 200) {
                    setDisableLoginBtn(false);
                    setErrorSignup({
                        error: true,
                        msg: json.error
                    });
                } else {
                    // navigate('/validate');
                    setSendButtonClick(true);
                }
            })
            .catch((e) => {
                console.log('errorUser', e);
                setDisableLoginBtn(false);
                setErrorSignup({
                    error: true,
                    msg: 'Error registering user. Try again later'
                });
            });
    };

    return (
        <>
            <NotificationToast showToast={showToast} msg={errorSignup.msg} fail={errorSignup.error} />
            <div className="background-image-container p-sm-3">
                <div className="signupDiv p-5">
                    <Image src={awayterImg} className="img-fluid" />
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {!sendButtonCick && showSpinner ?
                            <InnerForm
                                disableLoginBtn={disableLoginBtn}
                                errorSignup={errorSignup}
                                setErrorSignup={setErrorSignup}
                                navigate={navigate}
                                UserLang={UserLang}
                            />
                            : <>
                                <Spinner className="mt-3" animation="border" variant="success" />
                                <p className="emailText mt-3"> Check your email to validate your account.</p>
                            </>
                        }
                    </Formik>
                </div>
            </div>
        </>
    );
};

export default SignUpForm;
